import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { 
  Plus, Edit, Trash2, Search, Loader2, FileText, Upload, X, File
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ManageBlogV3 = () => {
  const { getAuthHeader } = useAuth();
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    category: 'Job Notifications',
    readTime: '5 min read'
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedPDF, setSelectedPDF] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');

  const categories = ['All', 'Job Notifications', 'Exam Updates', 'Results', 'Admit Cards', 'Study Tips', 'Current Affairs'];

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog`);
      const data = await response.json();
      setBlogPosts(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      setLoading(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        return;
      }
      setSelectedImage(file);
    }
  };

  const handlePDFSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('PDF size should be less than 10MB');
        return;
      }
      setSelectedPDF(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.excerpt) {
      alert('Please fill in all required fields');
      return;
    }

    // Convert editor content to HTML
    const content = draftToHtml(convertToRaw(editorState.getCurrentContent()));

    if (!content || content.trim() === '<p></p>\n' || content.trim() === '') {
      alert('Please add content to your blog post');
      return;
    }

    setSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('excerpt', formData.excerpt);
      formDataToSend.append('content', content);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('read_time', formData.readTime);
      
      if (selectedImage) {
        formDataToSend.append('image', selectedImage);
      }

      if (selectedPDF) {
        formDataToSend.append('pdf', selectedPDF);
      }

      const url = editingPost 
        ? `${API_URL}/api/blog/admin/${editingPost.id}`
        : `${API_URL}/api/blog/admin`;
      
      const method = editingPost ? 'PUT' : 'POST';

      const authHeader = getAuthHeader();
      const headers = {};
      if (authHeader.Authorization) {
        headers['Authorization'] = authHeader.Authorization;
      }

      const response = await fetch(url, {
        method,
        headers: headers,
        body: formDataToSend
      });

      if (!response.ok) {
        throw new Error('Failed to save blog post');
      }

      await fetchBlogPosts();
      resetForm();
      setShowForm(false);
      alert(editingPost ? 'Blog post updated successfully!' : 'Blog post created successfully!');
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to save blog post. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      excerpt: post.excerpt,
      category: post.category,
      readTime: post.read_time || '5 min read'
    });
    
    // Convert HTML to Draft.js content
    const contentBlock = htmlToDraft(post.content);
    if (contentBlock) {
      const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
      const editorState = EditorState.createWithContent(contentState);
      setEditorState(editorState);
    }
    
    setSelectedImage(null);
    setSelectedPDF(null);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/blog/admin/${id}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });

      if (response.ok) {
        await fetchBlogPosts();
        alert('Blog post deleted successfully!');
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('Failed to delete blog post');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      excerpt: '',
      category: 'Job Notifications',
      readTime: '5 min read'
    });
    setEditorState(EditorState.createEmpty());
    setSelectedImage(null);
    setSelectedPDF(null);
    setEditingPost(null);
  };

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'All' || post.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Manage Blog Posts</h1>
            <p className="text-gray-600">Create and manage blog posts with rich text formatting</p>
          </div>
          <Button 
            onClick={() => {
              resetForm();
              setShowForm(!showForm);
            }}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {showForm ? <X className="mr-2" size={20} /> : <Plus className="mr-2" size={20} />}
            {showForm ? 'Cancel' : 'Create New Post'}
          </Button>
        </div>

        {/* Create/Edit Form */}
        {showForm && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <FileText className="mr-2 text-red-600" size={24} />
                {editingPost ? 'Edit Blog Post' : 'Create New Blog Post'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Post Title *
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500"
                      placeholder="e.g., SSC CGL 2026 Notification"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category *
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500"
                    >
                      {categories.filter(cat => cat !== 'All').map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Excerpt (Short Summary) *
                  </label>
                  <textarea
                    value={formData.excerpt}
                    onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                    required
                    rows="2"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500"
                    placeholder="Brief summary for the blog list page"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Content * (Rich Text Editor)
                  </label>
                  <div className="border border-gray-300 rounded-md" style={{ minHeight: '400px' }}>
                    <Editor
                      editorState={editorState}
                      onEditorStateChange={setEditorState}
                      wrapperClassName="wrapper-class"
                      editorClassName="editor-class px-4"
                      toolbarClassName="toolbar-class"
                      toolbar={{
                        options: ['inline', 'blockType', 'fontSize', 'fontFamily', 'list', 'textAlign', 'colorPicker', 'link', 'embedded', 'emoji', 'image', 'remove', 'history'],
                        inline: {
                          options: ['bold', 'italic', 'underline', 'strikethrough', 'monospace']
                        },
                        blockType: {
                          inDropdown: true,
                          options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'Blockquote', 'Code']
                        },
                        fontSize: {
                          options: [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96]
                        },
                        list: {
                          options: ['unordered', 'ordered']
                        },
                        textAlign: {
                          options: ['left', 'center', 'right', 'justify']
                        },
                        colorPicker: {
                          colors: ['rgb(97,189,109)', 'rgb(26,188,156)', 'rgb(84,172,210)', 'rgb(44,130,201)',
                            'rgb(147,101,184)', 'rgb(71,85,119)', 'rgb(204,204,204)', 'rgb(65,168,95)', 'rgb(0,168,133)',
                            'rgb(61,142,185)', 'rgb(41,105,176)', 'rgb(85,57,130)', 'rgb(40,50,78)', 'rgb(0,0,0)',
                            'rgb(247,218,100)', 'rgb(251,160,38)', 'rgb(235,107,86)', 'rgb(226,80,65)', 'rgb(163,143,132)',
                            'rgb(239,239,239)', 'rgb(255,255,255)', 'rgb(250,197,28)', 'rgb(243,121,52)', 'rgb(209,72,65)',
                            'rgb(184,49,47)', 'rgb(124,112,107)', 'rgb(209,213,216)']
                        },
                        link: {
                          defaultTargetOption: '_blank'
                        }
                      }}
                      placeholder="Write your blog post content with formatting..."
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Read Time
                    </label>
                    <input
                      type="text"
                      value={formData.readTime}
                      onChange={(e) => setFormData({...formData, readTime: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500"
                      placeholder="e.g., 5 min read"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Featured Image
                    </label>
                    <input
                      type="file"
                      onChange={handleImageSelect}
                      accept=".jpg,.jpeg,.png,.webp"
                      className="w-full text-sm"
                    />
                    {selectedImage && (
                      <p className="text-xs text-green-600 mt-1">✓ {selectedImage.name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Attach PDF (Optional)
                    </label>
                    <input
                      type="file"
                      onChange={handlePDFSelect}
                      accept=".pdf"
                      className="w-full text-sm"
                    />
                    {selectedPDF && (
                      <p className="text-xs text-green-600 mt-1">✓ {selectedPDF.name}</p>
                    )}
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <Button 
                    type="submit" 
                    disabled={submitting}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 animate-spin" size={20} />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Upload className="mr-2" size={20} />
                        {editingPost ? 'Update Post' : 'Publish Post'}
                      </>
                    )}
                  </Button>
                  <Button 
                    type="button"
                    onClick={() => {
                      resetForm();
                      setShowForm(false);
                    }}
                    variant="outline"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Search and Filter */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search posts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Blog Posts List */}
        <Card>
          <CardHeader>
            <CardTitle>All Blog Posts ({filteredPosts.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <Loader2 className="animate-spin mx-auto mb-4" size={40} />
                <p>Loading blog posts...</p>
              </div>
            ) : filteredPosts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText size={48} className="mx-auto mb-4 opacity-50" />
                <p>No blog posts found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredPosts.map((post) => (
                  <div 
                    key={post.id} 
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{post.title}</h3>
                          <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                            {post.category}
                          </span>
                          {post.pdf_url && (
                            <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium flex items-center">
                              <File size={12} className="mr-1" />
                              PDF
                            </span>
                          )}
                        </div>
                        <p className="text-gray-600 text-sm mb-2">{post.excerpt}</p>
                        <div className="flex gap-4 text-xs text-gray-500">
                          <span>{new Date(post.created_at).toLocaleDateString()}</span>
                          <span>{post.read_time}</span>
                          <span>By {post.author_name}</span>
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button
                          onClick={() => handleEdit(post)}
                          size="sm"
                          variant="outline"
                          className="text-blue-600 hover:bg-blue-50"
                        >
                          <Edit size={16} />
                        </Button>
                        <Button
                          onClick={() => handleDelete(post.id)}
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default ManageBlogV3;
